import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Plus,
  Search,
  Download,
  Eye,
  Edit,
  Trash2,
  MapPin,
  Calendar,
  IndianRupee,
  Users,
  Clock,
  Briefcase,
  Building,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
  Copy,
  ExternalLink
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import JobForm from './JobForm';

const RecruitmentJobs = ({ onBack, userRole }) => {
  const [activeTab, setActiveTab] = useState('active');
  const [jobs, setJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);

  // Form state management moved to parent level
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    location: '',
    type: 'Full-time',
    experience: '',
    salary: '',
    description: '',
    requirements: [''],
    responsibilities: [''],
    closingDate: '',
    urgency: 'medium'
  });

  // Handle form data when editing - only update when editingJob actually changes
  useEffect(() => {
    if (editingJob) {
      setFormData({
        title: editingJob.title || '',
        department: editingJob.department || '',
        location: editingJob.location || '',
        type: editingJob.type || 'Full-time',
        experience: editingJob.experience || '',
        salary: editingJob.salary || '',
        description: editingJob.description || '',
        requirements: editingJob.requirements || [''],
        responsibilities: editingJob.responsibilities || [''],
        closingDate: editingJob.closingDate || '',
        urgency: editingJob.urgency || 'medium'
      });
    }
  }, [editingJob?.id]); // Only depend on the job ID, not the entire object


  // Mock job data
  useEffect(() => {
    const mockJobs = [
      {
        id: 'JOB001',
        title: 'Senior Software Engineer',
        department: 'IT Department',
        location: 'Mumbai Office',
        type: 'Full-time',
        experience: '3-5 years',
        salary: '₹8,00,000 - ₹12,00,000',
        description: 'We are looking for a Senior Software Engineer to join our dynamic IT team...',
        requirements: [
          'Bachelor\'s degree in Computer Science or related field',
          '3+ years of experience in software development',
          'Proficiency in React, Node.js, and MongoDB',
          'Strong problem-solving skills'
        ],
        responsibilities: [
          'Develop and maintain web applications',
          'Collaborate with cross-functional teams',
          'Code review and mentoring junior developers',
          'Participate in technical discussions and planning'
        ],
        status: 'active',
        postedDate: '2024-01-15',
        closingDate: '2024-02-15',
        applicationsCount: 45,
        postedBy: 'HR Manager',
        urgency: 'high'
      },
      {
        id: 'JOB002',
        title: 'Marketing Specialist',
        department: 'Marketing',
        location: 'Delhi Office',
        type: 'Full-time',
        experience: '2-4 years',
        salary: '₹5,00,000 - ₹8,00,000',
        description: 'Join our marketing team to drive brand awareness and lead generation...',
        requirements: [
          'Bachelor\'s degree in Marketing or related field',
          '2+ years of digital marketing experience',
          'Experience with Google Ads and social media marketing',
          'Strong analytical skills'
        ],
        responsibilities: [
          'Develop and execute marketing campaigns',
          'Manage social media presence',
          'Analyze marketing metrics and ROI',
          'Collaborate with sales team'
        ],
        status: 'active',
        postedDate: '2024-01-20',
        closingDate: '2024-02-20',
        applicationsCount: 28,
        postedBy: 'Marketing Manager',
        urgency: 'medium'
      },
      {
        id: 'JOB003',
        title: 'Financial Analyst',
        department: 'Finance',
        location: 'Bangalore Office',
        type: 'Full-time',
        experience: '1-3 years',
        salary: '₹4,50,000 - ₹7,00,000',
        description: 'We need a detail-oriented Financial Analyst to support our finance team...',
        requirements: [
          'Bachelor\'s degree in Finance, Accounting, or Economics',
          '1+ years of financial analysis experience',
          'Proficiency in Excel and financial modeling',
          'Strong attention to detail'
        ],
        responsibilities: [
          'Prepare financial reports and analysis',
          'Support budgeting and forecasting',
          'Monitor financial performance',
          'Assist in month-end closing processes'
        ],
        status: 'draft',
        postedDate: '2024-01-25',
        closingDate: '2024-02-25',
        applicationsCount: 0,
        postedBy: 'Finance Manager',
        urgency: 'low'
      },
      {
        id: 'JOB004',
        title: 'HR Executive',
        department: 'HR Department',
        location: 'Chennai Office',
        type: 'Full-time',
        experience: '1-2 years',
        salary: '₹3,50,000 - ₹5,50,000',
        description: 'Looking for an HR Executive to support recruitment and employee relations...',
        requirements: [
          'Bachelor\'s degree in HR or related field',
          '1+ years of HR experience',
          'Knowledge of labor laws and HR practices',
          'Excellent communication skills'
        ],
        responsibilities: [
          'Support recruitment processes',
          'Maintain employee records',
          'Assist in policy implementation',
          'Handle employee queries'
        ],
        status: 'closed',
        postedDate: '2024-01-05',
        closingDate: '2024-01-30',
        applicationsCount: 67,
        postedBy: 'HR Manager',
        urgency: 'medium'
      }
    ];
    setJobs(mockJobs);
  }, []);

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = filterDepartment === 'all' || job.department === filterDepartment;
    const matchesLocation = filterLocation === 'all' || job.location === filterLocation;
    const matchesTab = activeTab === 'all' || job.status === activeTab;
    return matchesSearch && matchesDepartment && matchesLocation && matchesTab;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      case 'paused': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'draft': return <FileText className="w-4 h-4" />;
      case 'closed': return <XCircle className="w-4 h-4" />;
      case 'paused': return <AlertCircle className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDeleteJob = (jobId) => {
    if (window.confirm('Are you sure you want to delete this job posting?')) {
      setJobs(prev => prev.filter(job => job.id !== jobId));
    }
  };

  const handleStatusChange = (jobId, newStatus) => {
    setJobs(prev => prev.map(job =>
      job.id === jobId ? { ...job, status: newStatus } : job
    ));
  };

  const generateJobLink = (jobId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/job-application/${jobId}`;
  };

  const copyJobLink = async (jobId) => {
    try {
      const link = generateJobLink(jobId);
      await navigator.clipboard.writeText(link);
      setCopiedLink(jobId);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = generateJobLink(jobId);
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedLink(jobId);
      setTimeout(() => setCopiedLink(null), 2000);
    }
  };

  const openJobLink = (jobId) => {
    const link = generateJobLink(jobId);
    window.open(link, '_blank');
  };

  // Reset form to default values
  const resetForm = () => {
    setFormData({
      title: '',
      department: '',
      location: '',
      type: 'Full-time',
      experience: '',
      salary: '',
      description: '',
      requirements: [''],
      responsibilities: [''],
      closingDate: '',
      urgency: 'medium'
    });
  };

  // Form management functions
  const handleFormSubmit = (e) => {
    e.preventDefault();
    const jobData = {
      ...formData,
      id: editingJob ? editingJob.id : `JOB${String(jobs.length + 1).padStart(3, '0')}`,
      status: 'draft',
      postedDate: new Date().toISOString().split('T')[0],
      applicationsCount: editingJob ? editingJob.applicationsCount : 0,
      postedBy: 'Current User'
    };

    if (editingJob) {
      setJobs(prev => prev.map(job => job.id === editingJob.id ? jobData : job));
    } else {
      setJobs(prev => [...prev, jobData]);
    }

    setShowJobForm(false);
    setEditingJob(null);
    resetForm(); // Reset form after submission
  };

  const addRequirement = () => {
    setFormData(prev => ({
      ...prev,
      requirements: [...prev.requirements, '']
    }));
  };

  const removeRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  const updateRequirement = (index, value) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.map((req, i) => i === index ? value : req)
    }));
  };

  const addResponsibility = () => {
    setFormData(prev => ({
      ...prev,
      responsibilities: [...prev.responsibilities, '']
    }));
  };

  const removeResponsibility = (index) => {
    setFormData(prev => ({
      ...prev,
      responsibilities: prev.responsibilities.filter((_, i) => i !== index)
    }));
  };

  const updateResponsibility = (index, value) => {
    setFormData(prev => ({
      ...prev,
      responsibilities: prev.responsibilities.map((resp, i) => i === index ? value : resp)
    }));
  };


  if (showJobForm) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center mb-6">
            <PermissionGate permission="recruitment" action="read">
              <Button
                variant="outline"
                onClick={() => {
                  setShowJobForm(false);
                  setEditingJob(null);
                  resetForm();
                }}
                className="mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </PermissionGate>
            <h1 className="text-2xl font-bold text-gray-900">
              {editingJob ? 'Edit Job Posting' : 'Create Job Posting'}
            </h1>
          </div>
          <JobForm
            formData={formData}
            setFormData={setFormData}
            editingJob={editingJob}
            handleFormSubmit={handleFormSubmit}
            resetForm={resetForm}
            setShowJobForm={setShowJobForm}
            setEditingJob={setEditingJob}
            addRequirement={addRequirement}
            removeRequirement={removeRequirement}
            updateRequirement={updateRequirement}
            addResponsibility={addResponsibility}
            removeResponsibility={removeResponsibility}
            updateResponsibility={updateResponsibility}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            {onBack && (
              <PermissionGate permission="recruitment" action="read">
                <Button
                  variant="outline"
                  onClick={onBack}
                  className="mr-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </PermissionGate>
            )}
            <h1 className="text-2xl font-bold text-gray-900">Job Postings</h1>
          </div>
          <PermissionGate permission="recruitment" action="create">
            <Button onClick={() => {
              setEditingJob(null);
              resetForm();
              setShowJobForm(true);
            }}>
              <Plus className="w-4 h-4 mr-2" />
              Create Job Posting
            </Button>
          </PermissionGate>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {[
                { id: 'active', label: 'Active Jobs', count: jobs.filter(j => j.status === 'active').length },
                { id: 'draft', label: 'Draft Jobs', count: jobs.filter(j => j.status === 'draft').length },
                { id: 'closed', label: 'Closed Jobs', count: jobs.filter(j => j.status === 'closed').length },
                { id: 'all', label: 'All Jobs', count: jobs.length }
              ].map((tab) => (
                <PermissionGate permission="recruitment" action="read" key={tab.id}>
                  <button
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    {tab.label}
                    <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                      {tab.count}
                    </span>
                  </button>
                </PermissionGate>
              ))}
            </nav>
          </div>

          {/* Filters */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search jobs by title, department, or ID..."
                    value={searchTerm}
                    maxLength={100}
                    onKeyPress={(e) => {
                      if (!/^[a-zA-Z0-9\s]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Only allow alphanumeric characters and spaces
                      value = value.replace(/[^a-zA-Z0-9\s]/g, '');
                      // Enforce max length
                      if (value.length > 100) {
                        value = value.substring(0, 100);
                      }
                      setSearchTerm(value);
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <select
                  value={filterDepartment}
                  onChange={(e) => setFilterDepartment(e.target.value)}
                  className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 truncate"
                >
                  <option value="all" className="truncate" title="All Departments">All Departments</option>
                  <option value="IT Department" className="truncate" title="IT Department">IT Department</option>
                  <option value="HR Department" className="truncate" title="HR Department">HR Department</option>
                  <option value="Finance" className="truncate" title="Finance">Finance</option>
                  <option value="Sales Department" className="truncate" title="Sales Department">Sales Department</option>
                  <option value="Marketing" className="truncate" title="Marketing">Marketing</option>
                  <option value="Operations" className="truncate" title="Operations">Operations</option>
                </select>
                <select
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Locations</option>
                  <option value="Mumbai Office">Mumbai Office</option>
                  <option value="Delhi Office">Delhi Office</option>
                  <option value="Bangalore Office">Bangalore Office</option>
                  <option value="Chennai Office">Chennai Office</option>
                  <option value="Pune Office">Pune Office</option>
                  <option value="Remote">Remote</option>
                </select>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Job List */}
        <div className="space-y-4">
          {filteredJobs.map((job) => (
            <div key={job.id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                      {getStatusIcon(job.status)}
                      <span className="ml-1 capitalize">{job.status}</span>
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUrgencyColor(job.urgency)}`}>
                      <span className="capitalize">{job.urgency} Priority</span>
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center">
                      <Building className="w-4 h-4 mr-1" />
                      {job.department}
                    </div>
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {job.location}
                    </div>
                    <div className="flex items-center">
                      <Briefcase className="w-4 h-4 mr-1" />
                      {job.type}
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {job.experience}
                    </div>
                    {job.salary && (
                      <div className="flex items-center">
                        <IndianRupee className="w-4 h-4 mr-1" />
                        {job.salary}
                      </div>
                    )}
                  </div>

                  <p className="text-gray-700 mb-3 line-clamp-2">{job.description}</p>

                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        Posted: {job.postedDate}
                      </div>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        Closes: {job.closingDate}
                      </div>
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-1" />
                        {job.applicationsCount} applicants
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  {/* Sharable Link Buttons */}
                  {job.status === 'active' && (
                    <>
                      <PermissionGate permission="recruitment" action="read">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyJobLink(job.id)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Copy application link"
                        >
                          {copiedLink === job.id ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </PermissionGate>
                      <PermissionGate permission="recruitment" action="read">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openJobLink(job.id)}
                          className="text-green-600 hover:text-green-700"
                          title="Open application page"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </PermissionGate>
                    </>
                  )}

                  <PermissionGate permission="recruitment" action="read">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedJob(job)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </PermissionGate>
                  <PermissionGate permission="recruitment" action="update">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingJob(job);
                        setShowJobForm(true);
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </PermissionGate>
                  {job.status === 'draft' && (
                    <PermissionGate permission="recruitment" action="update">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStatusChange(job.id, 'active')}
                        className="text-green-600 hover:text-green-700"
                      >
                        Publish
                      </Button>
                    </PermissionGate>
                  )}
                  {job.status === 'active' && (
                    <PermissionGate permission="recruitment" action="update">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStatusChange(job.id, 'closed')}
                        className="text-red-600 hover:text-red-700"
                      >
                        Close
                      </Button>
                    </PermissionGate>
                  )}
                  <PermissionGate permission="recruitment" action="delete">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteJob(job.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredJobs.length === 0 && (
          <div className="text-center py-12">
            <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No job postings found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterDepartment !== 'all' || filterLocation !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by creating your first job posting.'}
            </p>
          </div>
        )}
      </div>

      {/* Job Details Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 !mt-0">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Job Details</h3>
              <Button
                variant="outline"
                onClick={() => setSelectedJob(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">{selectedJob.title}</h4>
                <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedJob.status)}`}>
                    {getStatusIcon(selectedJob.status)}
                    <span className="ml-1 capitalize">{selectedJob.status}</span>
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUrgencyColor(selectedJob.urgency)}`}>
                    <span className="capitalize">{selectedJob.urgency} Priority</span>
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Department</label>
                  <p className="text-sm text-gray-900">{selectedJob.department}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Location</label>
                  <p className="text-sm text-gray-900">{selectedJob.location}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Employment Type</label>
                  <p className="text-sm text-gray-900">{selectedJob.type}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Experience</label>
                  <p className="text-sm text-gray-900">{selectedJob.experience}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Salary Range</label>
                  <p className="text-sm text-gray-900">{selectedJob.salary}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Applications</label>
                  <p className="text-sm text-gray-900">{selectedJob.applicationsCount}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <p className="text-sm text-gray-900">{selectedJob.description}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Requirements</label>
                <ul className="list-disc list-inside space-y-1">
                  {selectedJob.requirements.map((req, index) => (
                    <li key={index} className="text-sm text-gray-900">{req}</li>
                  ))}
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Responsibilities</label>
                <ul className="list-disc list-inside space-y-1">
                  {selectedJob.responsibilities.map((resp, index) => (
                    <li key={index} className="text-sm text-gray-900">{resp}</li>
                  ))}
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Posted Date</label>
                  <p className="text-sm text-gray-900">{selectedJob.postedDate}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Closing Date</label>
                  <p className="text-sm text-gray-900">{selectedJob.closingDate}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecruitmentJobs;